import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '@/context/LanguageContext';
import { Globe, Shield, Users, Building2, ChevronLeft, ChevronRight } from 'lucide-react';

const EMBASSY_IMAGE = 'https://customer-assets.emergentagent.com/job_cd6c22bb-693b-41c8-a61a-f7b4e9542f9d/artifacts/t1fyhjpv_ANGOLA%20DAY%2010%281%29.jpg';

const MISSION_SLIDES = [
  { icon: Globe, key: 'diplomatic_mission' },
  { icon: Shield, key: 'consular_protection' },
  { icon: Users, key: 'community_support' },
];

function MissionCarousel({ t }) {
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => setCurrent(c => (c + 1) % MISSION_SLIDES.length), 5000);
    return () => clearInterval(timer);
  }, []);

  const prev = () => setCurrent(c => (c - 1 + MISSION_SLIDES.length) % MISSION_SLIDES.length);
  const next = () => setCurrent(c => (c + 1) % MISSION_SLIDES.length);
  const slide = MISSION_SLIDES[current];
  const Icon = slide.icon;

  return (
    <div className="relative" data-testid="mission-carousel">
      <div className="bg-white border border-gray-100 rounded-md p-8 md:p-12 text-center min-h-[260px] flex flex-col items-center justify-center">
        <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-6">
          <Icon size={28} className="text-[#CC0000]" />
        </div>
        <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-4">{t(`about.${slide.key}`)}</h3>
        <p className="text-gray-500 leading-relaxed max-w-2xl mx-auto">{t(`about.${slide.key}_desc`)}</p>
      </div>
      <div className="flex items-center justify-center gap-4 mt-6">
        <button onClick={prev} className="w-9 h-9 bg-white border border-gray-200 rounded-full flex items-center justify-center text-gray-500 hover:text-[#CC0000] hover:border-[#CC0000] transition-colors" data-testid="mission-prev">
          <ChevronLeft size={18} />
        </button>
        <div className="flex gap-2">
          {MISSION_SLIDES.map((_, i) => (
            <button key={i} onClick={() => setCurrent(i)}
              className={`h-2 rounded-full transition-all ${i === current ? 'w-8 bg-[#CC0000]' : 'w-2 bg-gray-300 hover:bg-gray-400'}`} />
          ))}
        </div>
        <button onClick={next} className="w-9 h-9 bg-white border border-gray-200 rounded-full flex items-center justify-center text-gray-500 hover:text-[#CC0000] hover:border-[#CC0000] transition-colors" data-testid="mission-next">
          <ChevronRight size={18} />
        </button>
      </div>
    </div>
  );
}

export default function Embassy() {
  const { t } = useLanguage();

  return (
    <div className="min-h-screen bg-white" data-testid="embassy-page">
      {/* Hero */}
      <div className="relative h-[340px] md:h-[400px] overflow-hidden">
        <img src={EMBASSY_IMAGE} alt="Embaixada" className="w-full h-full object-cover" style={{ objectPosition: 'center 30%' }} />
        <div className="absolute inset-0 bg-black/55" />
        <div className="relative z-10 flex flex-col items-center justify-center h-full text-center text-white px-4">
          <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#FFD700] mb-3">{t('embassy.overline')}</p>
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight font-serif">{t('embassy.title')}</h1>
          <p className="text-base text-white/80 max-w-2xl mt-4">{t('embassy.subtitle')}</p>
        </div>
      </div>

      {/* Stats Bar */}
      <div className="bg-[#CC0000] text-white">
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-6 text-center">
            {[
              { value: '+50K', label: t('embassy.stat_citizens') },
              { value: '1975', label: t('embassy.stat_since') },
              { value: '10+', label: t('embassy.stat_services') },
            ].map(({ value, label }) => (
              <div key={label}>
                <p className="text-3xl font-bold text-[#FFD700]">{value}</p>
                <p className="text-xs text-red-100 mt-1">{label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* About the Embassy + Mission Carousel */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            <div>
              <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#CC0000] mb-3">{t('embassy.about_overline')}</p>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 tracking-tight font-serif mb-6">{t('embassy.about_title')}</h2>
              <div className="space-y-4 text-gray-600 leading-relaxed text-justify">
                <p>{t('embassy.about_p1')}</p>
                <p>{t('embassy.about_p2')}</p>
                <p>{t('embassy.about_p3')}</p>
              </div>
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-900 mb-4">{t('embassy.mission_title')}</h3>
              <MissionCarousel t={t} />
            </div>
          </div>
        </div>
      </section>

      {/* Sectores */}
      <section className="py-16 bg-[#F9FAFB]">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 tracking-tight font-serif">{t('embassy.sectors_title')}</h2>
            <div className="w-16 h-1 bg-[#CC0000] mx-auto mt-4" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[
              { icon: Building2, title: t('embassy.sector_consular'), desc: t('embassy.sector_consular_desc') },
              { icon: Globe, title: t('embassy.sector_political'), desc: t('embassy.sector_political_desc') },
              { icon: Users, title: t('embassy.sector_economic'), desc: t('embassy.sector_economic_desc') },
              { icon: Shield, title: t('embassy.sector_communication'), desc: t('embassy.sector_communication_desc') },
            ].map(({ icon: Icon, title, desc }) => (
              <div key={title} className="flex gap-4 p-6 bg-white border border-gray-100 rounded-md hover:border-[#CC0000]/20 hover:shadow-sm transition-all">
                <div className="w-12 h-12 bg-red-50 rounded-full flex items-center justify-center flex-shrink-0">
                  <Icon size={20} className="text-[#CC0000]" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">{title}</h3>
                  <p className="text-sm text-gray-500 mt-1">{desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
